// customers (platform target-model §9.1; ops parity backoffice/pages/customers-loyalty.md §1-§2, register W-69; field
// notes backoffice.customers.list and register.customers): a location's customer profiles as config written at the node
// or in the cloud (ADR-015), so a register can create one with the internet down. The profile is contact PII, so it is
// never sent to devices on the config feed; registers look customers up through the node. Visit stats are a rollup,
// never a stored column.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { CustomerAddressId, CustomerId, EmployeeId, EpochMs, LocationId, Money, OrderId, SignedQty } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten, ProposalRequired } from '../config/index.ts';

/** One saved address (legacy `customer_addresses`); exactly one is primary whenever a customer has any. */
export const CustomerAddress = z
  .object({
    addressId: CustomerAddressId,
    line1: z.string().trim().max(255),
    city: z.string().trim().max(255),
    state: z.string().trim().max(255),
    zip: z.string().trim().max(20),
    isDefault: z.boolean(),
  })
  .meta({ id: 'CustomerAddress' });

export const CustomerFields = z
  .object({
    firstName: z.string().trim().min(1).max(255),
    lastName: z.string().trim().max(255),
    email: z.string().trim().max(255),
    /** Digits only; empty when none. A save needs exactly 10 (core validateCustomer); imported rows may differ. */
    phone: z.string().regex(/^\d{0,15}$/),
    notes: z.string().max(2000),
    addresses: z.array(CustomerAddress).max(20),
  })
  .meta({ id: 'CustomerFields' });

export const CustomerView = CustomerFields.extend({ customerId: CustomerId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'CustomerView' });

// ---- errors ----------------------------------------------------------------------------------------------------------
/** A save core refuses: no first name, an email without a domain, a phone that is not 10 digits, notes over 2000. */
export const CustomersInvalid = defineError({
  code: 'customers.invalid', httpStatus: 422, message: 'errors.customers.invalid', retryable: false,
  details: z.object({ rule: z.string(), field: z.string().optional() }),
});

export const CustomerNotFound = defineError({
  code: 'customers.not_found', httpStatus: 404, message: 'errors.customers.not_found', retryable: false,
  details: z.object({ customerId: z.string() }),
});

const common = {
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], errors: [CustomersInvalid, CloudAuthoritative, ProposalRequired, CustomerNotFound],
  dryRun: true,
} as const;

// ---- commands --------------------------------------------------------------------------------------------------------
export const UpsertCustomer = defineCommand({
  ...common,
  name: 'customers.upsertCustomer',
  summary: 'Create a customer or change their name, contact details, notes and saved addresses.',
  input: z.object({ customerId: CustomerId, locationId: LocationId, changes: CustomerFields.partial() }),
  output: CustomerView,
  permission: 'customers.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['POST /v1/customers', 'PUT /v1/customers/{id}'],
});

export const DeleteCustomer = defineCommand({
  ...common,
  name: 'customers.deleteCustomer',
  summary: 'Delete a customer. Orders they were attached to keep their name.',
  input: z.object({ customerId: CustomerId, locationId: LocationId }),
  output: z.object({ id: CustomerId, deleted: z.literal(true) }),
  permission: 'customers.manage',
  risk: 'medium',
  agent: { expose: false },
  legacy: ['DELETE /v1/customers/{id}'],
});

// ---- queries ---------------------------------------------------------------------------------------------------------
export const ListCustomers = defineQuery({
  name: 'customers.list',
  summary: "The location's customers by name, with their contact details and addresses.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ customers: z.array(CustomerView) }),
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  permission: 'customers.manage',
  legacy: ['GET /v1/customers'],
});

export const CustomerDetail = defineQuery({
  name: 'customers.detail',
  summary: "A customer's profile, lifetime figures, orders, favourite items, payment methods and refunds (Customers › a customer).",
  input: z.object({ locationId: LocationId, customerId: CustomerId }),
  output: z.object({
    customer: CustomerView,
    stats: z.object({
      /** Orders that were not voided. */
      orderCount: z.int().nonnegative(),
      /** Their totals less what was refunded on them. */
      spend: Money,
      averageSpend: Money,
      firstOrderAt: EpochMs.nullable(),
      lastOrderAt: EpochMs.nullable(),
      /** Every order, voided too, a month since the first, to one decimal. */
      visitsPerMonth: z.number().nonnegative(),
      refundsIssued: z.int().nonnegative(),
      refunded: Money,
    }),
    orders: z.array(z.object({
      orderId: OrderId, number: z.string(), openedAt: EpochMs, total: Money, status: z.enum(['open', 'sent', 'closed', 'voided', 'abandoned']),
      /** How it was paid: each payment method once, in the order first used. */
      methods: z.array(z.string()),
      /**
       * B1609 (legacy main b4bfde902 CustomerOrdersTab's Payment cell): each payment that counts, newest first, by its short
       * name (core `tenderShortName`: Cash, Card, Check, Gift Card, House Account, Loyalty, or a custom tender type's name),
       * as legacy's `Order::tenders` (amount 0 or more, latest first). Absent before 2026-10-29.
       */
      tenderNames: z.array(z.string().max(255)).optional(),
    })),
    /**
     * Legacy's `(int) SUM(quantity)` and `SUM(subtotal)` by line name: a row whose returns outweigh its sales reads below
     * zero, as legacy shows it (#2639 round 3, B4).
     */
    favoriteItems: z.array(z.object({ productName: z.string(), quantity: SignedQty, revenue: Money })),
    paymentMethods: z.array(z.object({ method: z.string(), count: z.int().nonnegative(), paid: Money })),
    refunds: z.array(z.object({ refundId: z.string(), orderId: OrderId, orderNumber: z.string(), refunded: Money, requestedAt: EpochMs })),
  }),
  availability: 'location',
  scope: 'location',
  errors: [CustomerNotFound],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  permission: 'customers.manage',
  legacy: ['GET /v1/customers/{id}/detail', 'POST /v1/orders/lookup (customer_id)'],
});

export const ExportCustomers = defineQuery({
  name: 'customers.exportCustomers',
  summary: "The rows of Customers › Download Customers: each live customer's name, email, phone, latest order and creation time, oldest first.",
  input: z.object({ locationId: LocationId }),
  output: z.object({
    customers: z.array(z.object({
      customerId: CustomerId,
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
      /** The stored digits. */
      phone: z.string(),
      /** When their latest order was opened (every order is a visit, as on the detail page); legacy's column was always blank (B160). */
      lastVisitAt: EpochMs.nullable(),
      /** When the customer was created: the time in their UUIDv7 id (an imported customer's carries legacy `created_at`). */
      createdAt: EpochMs,
    })),
  }),
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  permission: 'customers.manage',
  legacy: ['GET /v1/download/customers'],
});

// ---- the register (through the node) ---------------------------------------------------------------------------------
// A register holds no profiles (target-model §9.1): it asks its node for the page it shows, the customer it opens and the
// possible duplicates of the one it creates. Any staff member at a register may do this, as legacy's device domain allowed.
const atRegister = { availability: 'location', scope: 'location', permission: { session: 'device' }, errors: [], agent: { expose: false } } as const;

export const SearchCustomers = defineQuery({
  ...atRegister,
  name: 'customers.search',
  summary: "A page of the location's customers: all in the order they were added, or those matching a name, email or phone.",
  input: z.object({
    locationId: LocationId,
    text: z.string().max(255),
    /** `any`: the Customers page box; `phone` and `name`: the sidebar's SEARCH BY tabs. */
    field: z.enum(['any', 'phone', 'name']),
    offset: z.int().nonnegative(),
    limit: z.int().min(1).max(50),
  }),
  output: z.object({ customers: z.array(CustomerView), matched: z.int().nonnegative() }),
  legacy: ['GET /v1/customers3'],
});

export const PotentialCustomerMatches = defineQuery({
  ...atRegister,
  name: 'customers.potentialMatches',
  summary: 'Customers who may be the one about to be created: a phone or email like the one typed (Potential Match Found).',
  input: z.object({ locationId: LocationId, phone: z.string().max(40), email: z.string().max(255) }),
  output: z.object({ customers: z.array(CustomerView) }),
});

export const CustomerSummary = z
  .object({ orderCount: z.int().nonnegative(), totalSpend: Money, averageSpend: Money, firstVisit: EpochMs.nullable(), lastVisit: EpochMs.nullable() })
  .meta({ id: 'CustomerSummary' });

export const CustomerProfile = defineQuery({
  ...atRegister,
  errors: [CustomerNotFound],
  name: 'customers.profile',
  summary: "A customer's details, visit summary and orders, newest first, as the register's customer sidebar shows them.",
  input: z.object({ locationId: LocationId, customerId: CustomerId }),
  output: z.object({
    customer: CustomerView,
    summary: CustomerSummary,
    orders: z.array(z.object({ orderId: OrderId, number: z.string(), openedAt: EpochMs, total: Money })),
  }),
  legacy: ['GET /v2/customers/{id}'],
});

/** One movement on a house account: a charge (the House Account tender) or a payment (a paid "House Account" line). */
export const HouseAccountEntry = z
  .object({
    /** The tender that charged it, or the paid line that paid it. */
    entryId: z.uuid(),
    orderId: OrderId.nullable(),
    amount: Money,
    at: EpochMs,
  })
  .meta({ id: 'HouseAccountEntry' });

export const HouseAccount = defineQuery({
  ...atRegister,
  errors: [CustomerNotFound],
  name: 'customers.houseAccount',
  summary: "A customer's house account: what they owe (charges less payments, a credit below zero) and every charge and payment, newest first.",
  input: z.object({ locationId: LocationId, customerId: CustomerId }),
  output: z.object({
    customerId: CustomerId,
    /** Charges less payments: owed to the house when positive (legacy `house_balance`). */
    balance: Money,
    charged: Money,
    paid: Money,
    charges: z.array(HouseAccountEntry),
    payments: z.array(HouseAccountEntry),
  }),
  legacy: ['GET /v2/customers/{id}/house-balance'],
});

/** A customer's house-account history from legacy (tenders of type house, `house_payments`), as the import carries it. */
export const LegacyHouseAccountHistory = z
  .object({ charges: z.array(HouseAccountEntry).max(20_000), payments: z.array(HouseAccountEntry).max(20_000) })
  .meta({ id: 'LegacyHouseAccountHistory' });

export const ImportHouseAccountHistory = defineCommand({
  ...common,
  availability: 'cloud',
  origin: 'cloud',
  name: 'customers.importHouseAccountHistory',
  cloudReason: 'Only the legacy import writes it, in the cloud, before the store moves; the store reads it with the profile.',
  summary: "Record a migrated customer's legacy house-account charges and payments, which their balance counts with the live ones.",
  input: z.object({ customerId: CustomerId, locationId: LocationId, history: LegacyHouseAccountHistory }),
  output: z.object({ customerId: CustomerId, charges: z.int().nonnegative(), payments: z.int().nonnegative() }),
  permission: 'customers.manage',
  risk: 'high',
  agent: { expose: false },
  legacy: ['house_payments', 'tenders (type house)'],
});

export const SaveCustomerAtRegister = defineCommand({
  ...common,
  name: 'customers.saveAtRegister',
  summary: 'Create or edit a customer from the register (Create & Close, Create & assign to Order, Update).',
  input: z.object({ customerId: CustomerId, locationId: LocationId, employeeId: EmployeeId, changes: CustomerFields.partial() }),
  output: CustomerView,
  permission: { session: 'device' },
  risk: 'low',
  agent: { expose: false },
  legacy: ['POST /v1/customers (device)', 'PUT /v1/customers/{id} (device)'],
});

export const customerCommands = [UpsertCustomer, DeleteCustomer, SaveCustomerAtRegister, ImportHouseAccountHistory] as const;
export const customerQueries = [ListCustomers, CustomerDetail, ExportCustomers, SearchCustomers, PotentialCustomerMatches, CustomerProfile, HouseAccount] as const;
